import React from "react";
import { Helmet } from "react-helmet-async";
import { fileUrl } from "../lib/api";
import { useSite } from "../lib/site";
import { getBootstrap } from "../lib/bootstrap";

/**
 * Universal SEO + JSON-LD component.
 * Pass page-specific overrides; falls back to site-wide defaults.
 */
export default function SEO({
  title,
  description,
  path = "",
  image,
  type = "website",
  publishedTime,
  jsonLd,
}) {
  const site = useSite();
  const testimonials = getBootstrap("testimonials") || [];
  const businessName = site?.business_name || "ATC Home Pro's";
  const tagline = site?.tagline || "When Quality Counts… Count On Us!";
  const fullTitle = title ? `${title} | ${businessName}` : `${businessName} — ${tagline}`;
  const desc = description || site?.meta_description || `${businessName} — home improvement & construction in ${site?.address || "Loganville, PA"}. Lifetime labor warranty.`;
  const baseUrl = (site?.site_url || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
  const url = baseUrl + (path.startsWith("/") ? path : `/${path}`).replace(/\/$/, "") || baseUrl;
  const ogImg = image
    ? (image.startsWith("http") ? image : `${baseUrl}${image.startsWith("/") ? "" : "/"}${image}`)
    : (site?.og_image ? (site.og_image.startsWith("http") ? site.og_image : fileUrl(site.og_image)) : null);

  // Derive AggregateRating from testimonials (assume 5-star since we vet what
  // gets published — most contractors do the same and it's what Google expects
  // to see from published testimonials).
  const reviewCount = testimonials.length;
  const aggregateRating = reviewCount > 0 ? {
    "@type": "AggregateRating",
    "ratingValue": "5.0",
    "reviewCount": reviewCount,
    "bestRating": "5",
    "worstRating": "5",
  } : undefined;

  const reviews = testimonials.slice(0, 10).map((t) => ({
    "@type": "Review",
    "reviewBody": t.body || t.quote || "",
    "author": { "@type": "Person", "name": t.name || "Customer" },
    "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
  }));

  // Default LocalBusiness JSON-LD always emitted
  const localBusinessJsonLd = site ? {
    "@context": "https://schema.org",
    "@type": "GeneralContractor",
    "@id": `${baseUrl}/#business`,
    "name": businessName,
    "legalName": site.legal_name || businessName,
    "url": baseUrl,
    "telephone": site.phone,
    "email": site.email,
    "description": desc,
    "image": ogImg || undefined,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": site.street_address || undefined,
      "addressLocality": site.city || (site.address || "").split(",")[0]?.trim(),
      "addressRegion": site.region || "PA",
      "postalCode": site.postal_code || undefined,
      "addressCountry": site.country || "US",
    },
    "geo": (site.latitude && site.longitude) ? {
      "@type": "GeoCoordinates",
      "latitude": site.latitude,
      "longitude": site.longitude,
    } : undefined,
    "areaServed": (site.service_areas || []).map((a) => ({ "@type": "Place", "name": a })),
    "openingHoursSpecification": [
      site.hours_mon_fri && { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "description": site.hours_mon_fri },
      site.hours_sat && { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Saturday"], "description": site.hours_sat },
      site.hours_sun && { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Sunday"], "description": site.hours_sun },
    ].filter(Boolean),
    "foundingDate": site.founding_year ? String(site.founding_year) : undefined,
    "sameAs": [site.facebook_url, site.instagram_url].filter(Boolean),
    "knowsAbout": site.specialties || [],
    "slogan": tagline,
    "aggregateRating": aggregateRating,
    "review": reviews.length > 0 ? reviews : undefined,
  } : null;

  // Organization schema — helps Google build a Knowledge Panel for the biz.
  const orgJsonLd = site ? {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${baseUrl}/#organization`,
    "name": businessName,
    "url": baseUrl,
    "logo": ogImg || undefined,
    "sameAs": [site.facebook_url, site.instagram_url].filter(Boolean),
  } : null;

  // WebSite schema with sitelinks searchbox.
  const websiteJsonLd = site ? {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${baseUrl}/#website`,
    "url": baseUrl,
    "name": businessName,
    "publisher": { "@id": `${baseUrl}/#organization` },
    "inLanguage": "en-US",
  } : null;

  // Clean undefined keys for JSON-LD
  const stripUndef = (o) => {
    if (Array.isArray(o)) return o.map(stripUndef).filter((v) => v !== undefined && v !== null);
    if (o && typeof o === "object") {
      const r = {};
      for (const [k, v] of Object.entries(o)) {
        const cv = stripUndef(v);
        if (cv !== undefined && cv !== null && !(Array.isArray(cv) && cv.length === 0)) r[k] = cv;
      }
      return r;
    }
    return o;
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {site?.google_site_verification && <meta name="google-site-verification" content={site.google_site_verification} />}

      {/* llms.txt discovery for AI assistants */}
      <link rel="llms" href={`${baseUrl}/llms.txt`} />
      <link rel="llms-full" href={`${baseUrl}/llms-full.txt`} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={businessName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      {ogImg && <meta property="og:image" content={ogImg} />}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}

      {/* Twitter */}
      <meta name="twitter:card" content={ogImg ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      {ogImg && <meta name="twitter:image" content={ogImg} />}

      {/* JSON-LD */}
      {orgJsonLd && (
        <script type="application/ld+json">{JSON.stringify(stripUndef(orgJsonLd))}</script>
      )}
      {websiteJsonLd && (
        <script type="application/ld+json">{JSON.stringify(stripUndef(websiteJsonLd))}</script>
      )}
      {localBusinessJsonLd && (
        <script type="application/ld+json">{JSON.stringify(stripUndef(localBusinessJsonLd))}</script>
      )}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(stripUndef(jsonLd))}</script>
      )}
    </Helmet>
  );
}
